/* ==========================================================================
   SUBJECT-AWARE GENERATOR
   --------------------------------------------------------------------------
   The base generator (exam-generator.js) builds numerical reasoning items.
   That is right for a maths placement test and wrong for the NCE or the PMP.

   This layer wraps it. Each exam is mapped to a subject family, and each
   family has its own templates drawing on real domain content. Anything
   without a family mapping falls through to the base generator unchanged,
   so nothing regresses.

   Loads AFTER exam-generator.js. Same public API: window.XP_GEN.generate.
   ========================================================================== */
(function () {
  'use strict';

  var BASE = window.XP_GEN;
  if (!BASE) return;

  var rng = BASE.rng;
  function pick(r, arr) { return arr[Math.floor(r() * arr.length) % arr.length]; }
  function intBetween(r, lo, hi) { return lo + Math.floor(r() * (hi - lo + 1)); }
  function shuffleWithAnswer(r, correct, wrongs, n) {
    var opts = [correct].concat(wrongs.slice(0, (n || 4) - 1));
    for (var i = opts.length - 1; i > 0; i--) {
      var j = Math.floor(r() * (i + 1));
      var t = opts[i]; opts[i] = opts[j]; opts[j] = t;
    }
    return { opts: opts, a: opts.indexOf(correct) };
  }
  function yt(q, label) {
    return { t: label || ('YouTube — ' + q), u: 'https://www.youtube.com/results?search_query=' + encodeURIComponent(q), k: 'video' };
  }
  var REF = {
    statpearls: { t: 'StatPearls (NCBI Bookshelf)', u: 'https://www.ncbi.nlm.nih.gov/books/NBK430685/', k: 'scholarly' },
    openstaxAP: { t: 'OpenStax — Anatomy & Physiology 2e', u: 'https://openstax.org/details/books/anatomy-and-physiology-2e', k: 'scholarly' },
    openstaxAlg: { t: 'OpenStax — Elementary Algebra 2e', u: 'https://openstax.org/details/books/elementary-algebra-2e', k: 'scholarly' },
    purdue: { t: 'Purdue OWL — Grammar & Mechanics', u: 'https://owl.purdue.edu/owl/general_writing/grammar/index.html', k: 'reference' },
    pmi: { t: 'PMI — Standards and the PMBOK Guide', u: 'https://www.pmi.org/standards', k: 'scholarly' },
    nist: { t: 'NIST — Cybersecurity Framework', u: 'https://www.nist.gov/cyberframework', k: 'scholarly' },
    aca: { t: 'ACA Code of Ethics', u: 'https://www.counseling.org/resources/aca-code-of-ethics.pdf', k: 'scholarly' },
    hud: { t: 'HUD — Fair Housing Act', u: 'https://www.hud.gov/program_offices/fair_housing_equal_opp/fair_housing_act_overview', k: 'scholarly' },
    mw: { t: 'Merriam-Webster Dictionary', u: 'https://www.merriam-webster.com/', k: 'reference' }
  };

  /* =====================================================================
     DOMAIN DATA
     ===================================================================== */

  /* Each lab carries explicit abnormal bands. A percentage deviation is
     wrong for narrow-range analytes — it produced a sodium of 183, which is
     not a value any student will be asked to interpret. */
  var LABS = [
    { n: 'Sodium', disp: 'sodium', lo: 135, hi: 145, u: 'mEq/L', lows: [118, 122, 126, 129, 132], highs: [148, 152, 156, 160],
      low: 'confusion, seizures, muscle cramps', high: 'thirst, dry mucous membranes, restlessness' },
    { n: 'Potassium', disp: 'potassium', lo: 3.5, hi: 5.0, u: 'mEq/L', lows: [2.4, 2.7, 2.9, 3.1], highs: [5.8, 6.2, 6.6, 7.1],
      low: 'muscle weakness, flat T waves, dysrhythmias', high: 'peaked T waves, widened QRS, cardiac arrest' },
    { n: 'Calcium', disp: 'calcium', lo: 9.0, hi: 10.5, u: 'mg/dL', lows: [6.8, 7.4, 7.9, 8.3], highs: [11.4, 12.1, 13.0, 13.8],
      low: 'tetany, positive Chvostek and Trousseau signs', high: 'lethargy, kidney stones, constipation' },
    { n: 'Magnesium', disp: 'magnesium', lo: 1.3, hi: 2.1, u: 'mEq/L', lows: [0.7, 0.9, 1.0, 1.1], highs: [2.6, 3.1, 3.8, 4.4],
      low: 'tremor, hyperactive reflexes, torsades de pointes', high: 'loss of deep tendon reflexes, respiratory depression' },
    { n: 'Haemoglobin', disp: 'haemoglobin (adult male)', lo: 14, hi: 18, u: 'g/dL', lows: [6.8, 8.2, 9.4, 11.0, 12.4], highs: [19.5, 20.4, 21.2],
      low: 'fatigue, pallor, tachycardia', high: 'increased blood viscosity and thrombosis risk' },
    { n: 'Platelet count', disp: 'platelet count', lo: 150, hi: 400, u: 'x10\u00b3/\u00b5L', lows: [18, 34, 62, 88, 118], highs: [520, 640, 780],
      low: 'petechiae, bruising and bleeding risk', high: 'increased clotting risk' },
    { n: 'Fasting glucose', disp: 'fasting blood glucose', lo: 70, hi: 100, u: 'mg/dL', lows: [38, 44, 52, 58, 64], highs: [148, 186, 240, 310],
      low: 'diaphoresis, tremor, confusion', high: 'polyuria, polydipsia, blurred vision' },
    { n: 'Creatinine', disp: 'serum creatinine', lo: 0.6, hi: 1.2, u: 'mg/dL', lows: [0.3, 0.4, 0.5], highs: [1.9, 2.6, 3.4, 4.8],
      low: 'reduced muscle mass rather than renal pathology', high: 'impaired renal function and fluid overload' },
    { n: 'BUN', disp: 'blood urea nitrogen (BUN)', lo: 10, hi: 20, u: 'mg/dL', lows: [4, 6, 8], highs: [28, 36, 45, 58],
      low: 'overhydration or liver disease', high: 'dehydration, renal impairment or GI bleeding' },
    { n: 'White blood cell count', disp: 'white blood cell count', lo: 5000, hi: 10000, u: '/mm\u00b3', lows: [1800, 2600, 3400, 4200], highs: [14000, 18000, 23000, 29000],
      low: 'infection risk requiring neutropenic precautions', high: 'active infection or inflammation' }
  ];

  /* Abnormal values are drawn from clinically plausible bands, not from a
     blind multiplier. A multiplier produced things like a body temperature
     of 28 degrees, which is a fatal reading rather than a practice item. */
  var VITALS = [
    { subj: 'An adult client\u2019s', n: 'respiratory rate', lo: 12, hi: 20, u: 'breaths/min', lows: [6, 7, 8, 9, 10], highs: [24, 26, 28, 30, 34] },
    { subj: 'An adult client\u2019s', n: 'heart rate', lo: 60, hi: 100, u: 'bpm', lows: [42, 46, 50, 52, 55], highs: [110, 118, 126, 134, 142] },
    { subj: 'A newborn\u2019s', n: 'heart rate', lo: 120, hi: 160, u: 'bpm', lows: [88, 96, 104, 110], highs: [172, 180, 188, 196] },
    { subj: 'An adult client\u2019s', n: 'oral temperature', lo: 36.5, hi: 37.5, u: '\u00b0C', lows: [34.8, 35.2, 35.6, 36.0], highs: [38.2, 38.7, 39.1, 39.6, 40.2] },
    { subj: 'An infant\u2019s', n: 'respiratory rate', lo: 30, hi: 60, u: 'breaths/min', lows: [18, 22, 25, 28], highs: [66, 72, 78, 84] },
    { subj: 'An adult client\u2019s', n: 'oxygen saturation', lo: 95, hi: 100, u: '%', lows: [82, 86, 88, 90, 92], highs: [] }
  ];

  var PRECAUTIONS = [
    { d: 'tuberculosis', p: 'Airborne', ppe: 'N95 respirator and a negative-pressure room' },
    { d: 'measles', p: 'Airborne', ppe: 'N95 respirator and a negative-pressure room' },
    { d: 'varicella (chickenpox)', p: 'Airborne', ppe: 'N95 respirator and a negative-pressure room' },
    { d: 'influenza', p: 'Droplet', ppe: 'a surgical mask within 1 metre of the client' },
    { d: 'pertussis', p: 'Droplet', ppe: 'a surgical mask within 1 metre of the client' },
    { d: 'bacterial meningitis', p: 'Droplet', ppe: 'a surgical mask within 1 metre of the client' },
    { d: 'Clostridioides difficile', p: 'Contact', ppe: 'gown and gloves, with soap-and-water handwashing rather than alcohol gel' },
    { d: 'MRSA wound infection', p: 'Contact', ppe: 'gown and gloves' },
    { d: 'scabies', p: 'Contact', ppe: 'gown and gloves' }
  ];

  var DRUGS = [
    { n: 'furosemide', c: 'loop diuretic', w: 'hypokalaemia', t: 'Monitor potassium and watch for muscle weakness and dysrhythmias.' },
    { n: 'lisinopril', c: 'ACE inhibitor', w: 'a persistent dry cough and hyperkalaemia', t: 'A dry cough is the classic reason for switching to an ARB.' },
    { n: 'metoprolol', c: 'beta blocker', w: 'bradycardia', t: 'Hold and reassess if the apical pulse is below 60.' },
    { n: 'warfarin', c: 'anticoagulant', w: 'bleeding', t: 'Monitor INR; vitamin K is the antidote.' },
    { n: 'digoxin', c: 'cardiac glycoside', w: 'toxicity worsened by low potassium', t: 'Check apical pulse for a full minute before giving.' },
    { n: 'prednisone', c: 'corticosteroid', w: 'hyperglycaemia and infection risk', t: 'Never stop abruptly — taper to avoid adrenal crisis.' },
    { n: 'levothyroxine', c: 'thyroid hormone', w: 'signs of hyperthyroidism if over-replaced', t: 'Take on an empty stomach, same time each morning.' },
    { n: 'phenytoin', c: 'anticonvulsant', w: 'gingival hyperplasia and ataxia', t: 'Narrow therapeutic range of 10 to 20 mcg/mL.' },
    { n: 'morphine', c: 'opioid analgesic', w: 'respiratory depression', t: 'Naloxone is the antidote; assess respiratory rate before each dose.' },
    { n: 'albuterol', c: 'short-acting beta-2 agonist', w: 'tachycardia and tremor', t: 'Rescue inhaler — used before the corticosteroid inhaler.' }
  ];

  var VOCAB = [
    { w: 'ameliorate', d: 'to make better', wrong: ['to worsen', 'to postpone', 'to confuse'] },
    { w: 'laconic', d: 'using very few words', wrong: ['long-winded', 'unfriendly', 'careless'] },
    { w: 'prudent', d: 'showing careful judgement', wrong: ['reckless', 'generous', 'talkative'] },
    { w: 'obstinate', d: 'stubbornly refusing to change', wrong: ['easily persuaded', 'unusually clever', 'openly hostile'] },
    { w: 'candid', d: 'honest and straightforward', wrong: ['secretive', 'hesitant', 'formal'] },
    { w: 'tenacious', d: 'holding firmly, persistent', wrong: ['easily discouraged', 'physically weak', 'widely admired'] },
    { w: 'benign', d: 'gentle and harmless', wrong: ['dangerous', 'enormous', 'temporary'] },
    { w: 'frugal', d: 'careful with money', wrong: ['wasteful', 'wealthy', 'dishonest'] },
    { w: 'ambiguous', d: 'open to more than one interpretation', wrong: ['perfectly clear', 'deliberately false', 'extremely brief'] },
    { w: 'diligent', d: 'showing steady, careful effort', wrong: ['lazy', 'brilliant', 'anxious'] },
    { w: 'exacerbate', d: 'to make a problem worse', wrong: ['to relieve', 'to explain', 'to delay'] },
    { w: 'lucid', d: 'clear and easy to understand', wrong: ['confusing', 'lengthy', 'dishonest'] },
    { w: 'meticulous', d: 'extremely careful about detail', wrong: ['careless', 'hurried', 'indifferent'] },
    { w: 'affable', d: 'friendly and easy to talk to', wrong: ['hostile', 'silent', 'wealthy'] },
    { w: 'transient', d: 'lasting only a short time', wrong: ['permanent', 'enormous', 'repeated'] },
    { w: 'astute', d: 'shrewd and perceptive', wrong: ['naive', 'generous', 'aggressive'] }
  ];

  var GRAMMAR = [
    { q: 'their / there / they\'re', correct: 'They\'re bringing their notes over there.', wrong: ['There bringing there notes over their.', 'Their bringing they\'re notes over there.', 'They\'re bringing there notes over their.'], why: '<strong>They\'re</strong> = they are. <strong>Their</strong> = possessive. <strong>There</strong> = place.' },
    { q: 'its / it\'s', correct: 'The committee published its report, and it\'s available online.', wrong: ['The committee published it\'s report, and its available online.', 'The committee published its report, and its available online.', 'The committee published it\'s report, and it\'s available online.'], why: '<strong>It\'s</strong> is only ever "it is" or "it has". <strong>Its</strong> is the possessive — no apostrophe, like <em>his</em> and <em>hers</em>.' },
    { q: 'subject-verb agreement', correct: 'The list of required documents is on the desk.', wrong: ['The list of required documents are on the desk.', 'The lists of required document is on the desk.', 'The list of required documents were on the desk.'], why: 'The subject is <strong>list</strong> (singular). "Of required documents" is a prepositional phrase — cross it out and the agreement is obvious.' },
    { q: 'who / whom', correct: 'To whom should I address the letter?', wrong: ['To who should I address the letter?', 'To whom should I address the letter to?', 'Whom should I address the letter?'], why: 'After a preposition, use <strong>whom</strong>. Test by answering with him/her (whom) or he/she (who).' },
    { q: 'fewer / less', correct: 'Fewer students enrolled, so there was less demand.', wrong: ['Less students enrolled, so there was fewer demand.', 'Fewer students enrolled, so there was fewer demand.', 'Less students enrolled, so there was less demand.'], why: '<strong>Fewer</strong> for countable nouns, <strong>less</strong> for uncountable quantities.' },
    { q: 'parallel structure', correct: 'She enjoys reading, writing and hiking.', wrong: ['She enjoys reading, to write and hiking.', 'She enjoys to read, writing and hikes.', 'She enjoys reading, writes and to hike.'], why: 'Items in a series must share the same grammatical form — here, all gerunds.' },
    { q: 'apostrophe with plurals', correct: 'The nurses\' station was relocated.', wrong: ['The nurse\'s station was relocated (referring to several nurses).', 'The nurses station was relocated.', 'The nurses\'s station was relocated.'], why: 'Plural noun ending in -s takes the apostrophe <strong>after</strong> the s.' },
    { q: 'affect / effect', correct: 'The policy will affect morale; its effect may take months to appear.', wrong: ['The policy will effect morale; its affect may take months to appear.', 'The policy will affect morale; its affect may take months to appear.', 'The policy will effect morale; its effect may take months to appear.'], why: '<strong>Affect</strong> is usually the verb, <strong>effect</strong> usually the noun. "RAVEN" — Remember, Affect Verb, Effect Noun.' }
  ];

  var PM_TERMS = [
    { t: 'Critical path', d: 'the longest sequence of dependent activities, which determines the shortest possible project duration', wrong: ['the most expensive sequence of activities', 'the sequence with the most float', 'the list of highest-risk activities'] },
    { t: 'Float (slack)', d: 'the amount of time an activity can slip without delaying the project finish', wrong: ['the buffer added to every cost estimate', 'the time spent on rework', 'the gap between planned and actual start'] },
    { t: 'Scope creep', d: 'uncontrolled expansion of scope without adjustment to time, cost and resources', wrong: ['an approved increase in scope with a matching budget rise', 'the process of decomposing scope into work packages', 'the gap between planned and delivered quality'] },
    { t: 'Risk mitigation', d: 'acting to reduce the probability or impact of a threat', wrong: ['eliminating the threat entirely by changing the plan', 'transferring the financial consequence to a third party', 'accepting the threat and setting aside a reserve'] },
    { t: 'A stakeholder register', d: 'a document recording identified stakeholders, their interests, influence and engagement strategy', wrong: ['a log of all approved change requests', 'a breakdown of project deliverables into work packages', 'a record of lessons learned at project close'] },
    { t: 'A work breakdown structure', d: 'a hierarchical decomposition of the total scope into deliverables and work packages', wrong: ['a sequenced list of activities with durations', 'an organisation chart of the project team', 'a register of project risks and responses'] },
    { t: 'The RACI chart', d: 'a matrix assigning who is Responsible, Accountable, Consulted and Informed for each activity', wrong: ['a technique for estimating three-point durations', 'a method of measuring earned value', 'a log of quality defects and their resolution'] },
    { t: 'A milestone', d: 'a significant point or event with zero duration', wrong: ['an activity lasting longer than one reporting period', 'the total float on the critical path', 'a deliverable that has failed quality review'] }
  ];

  var IT_TERMS = [
    { t: 'DHCP', d: 'automatically assigns IP addresses to devices on a network', wrong: ['resolves domain names to IP addresses', 'encrypts traffic between two endpoints', 'translates private addresses to a public one'] },
    { t: 'DNS', d: 'resolves human-readable domain names into IP addresses', wrong: ['assigns IP addresses dynamically', 'filters traffic based on rule sets', 'provides redundant disk storage'] },
    { t: 'A firewall', d: 'filters network traffic according to a defined rule set', wrong: ['assigns IP addresses to clients', 'converts analogue signals to digital', 'stores frequently accessed web content'] },
    { t: 'RAID 1', d: 'mirrors data across two drives so either can fail without loss', wrong: ['stripes data across drives with no redundancy', 'stripes data with distributed parity across three or more drives', 'combines drives into a single larger volume with no fault tolerance'] },
    { t: 'Multifactor authentication', d: 'requires evidence from two or more distinct categories — something you know, have, or are', wrong: ['requires two different passwords', 'encrypts credentials in transit', 'restricts logins to specific IP ranges'] },
    { t: 'A VPN', d: 'creates an encrypted tunnel across an untrusted network', wrong: ['assigns MAC addresses to network interfaces', 'compresses files to save bandwidth', 'monitors and logs failed login attempts'] },
    { t: 'Ransomware', d: 'encrypts a victim\'s files and demands payment for the key', wrong: ['secretly records keystrokes to steal credentials', 'floods a service with traffic until it fails', 'replicates itself across a network without user action'] },
    { t: 'A subnet mask', d: 'identifies which portion of an IP address refers to the network and which to the host', wrong: ['hides a device\'s MAC address from the network', 'sets the maximum transmission unit for a link', 'assigns priority to time-sensitive traffic'] }
  ];

  var COUNSEL = [
    { t: 'Unconditional positive regard', d: 'accepting the client fully without judgement, a core condition in person-centred therapy', wrong: ['agreeing with everything the client says', 'withholding all personal reaction from the client', 'setting firm limits on acceptable client behaviour'] },
    { t: 'Countertransference', d: 'the counsellor\'s own unresolved feelings being projected onto the client', wrong: ['the client redirecting feelings about others onto the counsellor', 'the counsellor deliberately modelling a behaviour', 'a client\'s resistance to interpretation'] },
    { t: 'Transference', d: 'the client redirecting feelings about a significant other onto the counsellor', wrong: ['the counsellor projecting personal feelings onto the client', 'the client\'s conscious imitation of the counsellor', 'a rupture in the therapeutic alliance'] },
    { t: 'Informed consent', d: 'the client\'s agreement to treatment after being told its nature, risks, limits and alternatives', wrong: ['written permission to release records to a third party', 'the counsellor\'s duty to warn an identified victim', 'documentation that a session took place'] },
    { t: 'A genogram', d: 'a diagram mapping family relationships and patterns across generations', wrong: ['a standardised measure of depressive symptoms', 'a written contract of therapy goals', 'a chart tracking mood over time'] },
    { t: 'Cognitive restructuring', d: 'identifying and modifying distorted automatic thoughts', wrong: ['gradual exposure to a feared stimulus', 'reinforcing a behaviour with a desired reward', 'exploring unconscious early-childhood conflict'] },
    { t: 'Systematic desensitisation', d: 'pairing relaxation with a graded hierarchy of feared stimuli', wrong: ['confronting the most feared stimulus immediately and at full intensity', 'disputing irrational beliefs directly', 'reframing a symptom as serving a family function'] },
    { t: 'Dual relationship', d: 'a counsellor holding a second, non-professional role with a client, which risks impaired judgement', wrong: ['seeing two members of the same family separately with consent', 'consulting a supervisor about a difficult case', 'referring a client to a specialist colleague'] }
  ];

  var RE_TERMS = [
    { t: 'Fee simple absolute', d: 'the most complete form of ownership, of unlimited duration and freely transferable', wrong: ['ownership lasting only for the life of a named person', 'a right to use another\'s land for a specific purpose', 'a tenant\'s right to occupy for a fixed term'] },
    { t: 'An easement', d: 'a right to use another party\'s land for a specific purpose', wrong: ['a financial claim against a property securing a debt', 'a private restriction recorded in the deed', 'the government\'s power to take property for public use'] },
    { t: 'A lien', d: 'a financial claim against a property that secures payment of a debt', wrong: ['a right of way across a neighbour\'s land', 'a limitation on how land may be developed', 'the transfer of title on death without a will'] },
    { t: 'Eminent domain', d: 'the government power to take private property for public use with just compensation', wrong: ['the state\'s claim to property when an owner dies intestate with no heirs', 'the right of a lender to force a sale on default', 'the authority to regulate land use through zoning'] },
    { t: 'Escheat', d: 'property passing to the state when an owner dies with no will and no heirs', wrong: ['taking private property for public use with compensation', 'a co-owner\'s right to force a partition sale', 'the seller\'s duty to disclose known defects'] },
    { t: 'Steering', d: 'directing prospective buyers toward or away from neighbourhoods based on a protected class', wrong: ['inducing panic selling by suggesting a neighbourhood is changing', 'refusing to lend in a defined geographic area', 'charging different commission rates to different clients'] },
    { t: 'Blockbusting', d: 'inducing owners to sell by suggesting that people of a particular class are moving into the area', wrong: ['guiding buyers to particular neighbourhoods based on race', 'denying loans based on the location of the property', 'advertising a property only to selected buyers'] },
    { t: 'Redlining', d: 'refusing to lend or insure in a defined geographic area, typically on a discriminatory basis', wrong: ['steering buyers away from certain neighbourhoods', 'inducing panic selling for profit', 'setting a listing price below market value'] }
  ];

  /* =====================================================================
     TEMPLATES
     ===================================================================== */

  function tLabValue(r, d) {
    var L = pick(r, LABS);
    var high = r() > 0.5;
    var band = high ? L.highs : L.lows;
    var val = band[Math.floor(r() * band.length) % band.length];
    var correct = high ? L.high : L.low;
    var wrongs = [
      high ? L.low : L.high,
      'No clinical significance — this is within the expected range',
      'An expected finding in a healthy adult that requires no action'
    ];
    var s = shuffleWithAnswer(r, correct, wrongs, 4);
    return {
      section: 'labvalues', difficulty: d, type: 'mcq',
      q: 'A client\u2019s ' + L.disp + ' is ' + val + ' ' + L.u + '. Which findings should the nurse anticipate?',
      opts: s.opts, a: s.a,
      why: '<p>The expected range for ' + L.disp + ' is <strong>' + L.lo + ' to ' + L.hi + ' ' + L.u + '</strong>. A value of ' + val + ' is <strong>' + (high ? 'above' : 'below') + '</strong> that range.</p>' +
        '<p>' + (high ? 'Elevation' : 'Depletion') + ' typically produces <strong>' + correct + '</strong>.</p>' +
        '<p>The distractor listing the opposite set of findings is the one to watch — it is correct physiology attached to the wrong direction. Read the value against the range before you read the options.</p>',
      refs: [REF.statpearls, yt(L.n + ' normal range nursing signs symptoms', 'Video \u2014 ' + L.n + ' interpretation')]
    };
  }

  function tPrecaution(r, d) {
    var P = pick(r, PRECAUTIONS);
    var all = ['Airborne', 'Droplet', 'Contact', 'Standard precautions only'];
    var wrongs = all.filter(function (x) { return x !== P.p; });
    var s = shuffleWithAnswer(r, P.p, wrongs, 4);
    return {
      section: 'infection', difficulty: d, type: 'mcq',
      q: 'A client is admitted with ' + P.d + '. Which category of transmission-based precautions is required?',
      opts: s.opts, a: s.a,
      why: '<p>' + P.d.charAt(0).toUpperCase() + p_rest(P.d) + ' requires <strong>' + P.p + ' precautions</strong> — ' + P.ppe + '.</p>' +
        '<p>The three categories are worth holding as a single map:</p>' +
        '<ul><li><strong>Airborne</strong> — TB, measles, varicella. N95 plus negative pressure.</li>' +
        '<li><strong>Droplet</strong> — influenza, pertussis, meningitis. Surgical mask.</li>' +
        '<li><strong>Contact</strong> — C. difficile, MRSA, scabies. Gown and gloves.</li></ul>' +
        '<p>Standard precautions apply to every client regardless, so they are never the complete answer when a specific transmissible organism is named.</p>',
      refs: [{ t: 'CDC — Transmission-Based Precautions', u: 'https://www.cdc.gov/infection-control/hcp/basics/transmission-based-precautions.html', k: 'scholarly' }, yt('isolation precautions airborne droplet contact nursing', 'Video — Isolation precautions')]
    };
  }
  function p_rest(s) { return s.slice(1); }

  function tDrug(r, d) {
    var D = pick(r, DRUGS);
    var wrongs = DRUGS.filter(function (x) { return x.c !== D.c; }).slice(0, 3).map(function (x) { return x.c; });
    var s = shuffleWithAnswer(r, D.c, wrongs, 4);
    return {
      section: 'pharmacology', difficulty: d, type: 'mcq',
      q: 'A client is prescribed ' + D.n + '. This medication belongs to which class?',
      opts: s.opts, a: s.a,
      why: '<p><strong>' + D.n.charAt(0).toUpperCase() + D.n.slice(1) + '</strong> is a <strong>' + D.c + '</strong>.</p>' +
        '<p>The adverse effect to monitor for is <strong>' + D.w + '</strong>.</p>' +
        '<p><em>Key nursing consideration:</em> ' + D.t + '</p>',
      refs: [REF.statpearls, yt(D.n + ' nursing considerations pharmacology', 'Video — ' + D.n)]
    };
  }

  function tVital(r, d) {
    var V = pick(r, VITALS);
    var high = V.highs.length ? r() > 0.5 : false;
    var band = high ? V.highs : V.lows;
    var val = band[Math.floor(r() * band.length) % band.length];
    var correct = 'Outside the expected range — report and reassess';
    var wrongs = ['Within the expected range — document and continue monitoring', 'Expected only during sleep', 'Expected only after exercise'];
    var s = shuffleWithAnswer(r, correct, wrongs, 4);
    return {
      section: 'assessment', difficulty: d, type: 'mcq',
      q: V.subj + ' ' + V.n + ' is measured at ' + val + ' ' + V.u + '. How should the nurse interpret this?',
      opts: s.opts, a: s.a,
      why: '<p>The expected range for this ' + V.n + ' is <strong>' + V.lo + ' to ' + V.hi + ' ' + V.u + '</strong>. A reading of ' + val + ' falls <strong>' + (high ? 'above' : 'below') + '</strong> it.</p>' +
        '<p>An out-of-range vital sign is assessed in context — trend, baseline, and accompanying symptoms all matter — but it is never simply documented and dismissed.</p>' +
        '<p>Age-specific ranges are examined heavily. Newborn and infant values differ substantially from adult values, and using the adult range on a paediatric client is the error these items are built around.</p>',
      refs: [REF.openstaxAP, yt('normal vital signs by age range nursing', 'Video — Vital signs by age')]
    };
  }

  function tVocab(r, d) {
    var V = pick(r, VOCAB);
    var s = shuffleWithAnswer(r, V.d, V.wrong, 4);
    return {
      section: 'vocabulary', difficulty: d, type: 'mcq',
      q: '"' + V.w.toUpperCase() + '" most nearly means:',
      opts: s.opts, a: s.a,
      why: '<p><strong>' + V.w.charAt(0).toUpperCase() + V.w.slice(1) + '</strong> means <strong>' + V.d + '</strong>.</p>' +
        '<p>Note that one distractor is a near-antonym. That is standard design on vocabulary items — if you half-remember a word as "something to do with effort" you can be pulled to the opposite pole.</p>' +
        '<p>When a word is unfamiliar, try it in a phrase you have actually heard, and check whether the sentence would carry a positive or negative charge.</p>',
      refs: [REF.mw]
    };
  }

  function tGrammar(r, d) {
    var G = pick(r, GRAMMAR);
    var s = shuffleWithAnswer(r, G.correct, G.wrong, 4);
    return {
      section: 'grammar', difficulty: d, type: 'mcq',
      q: 'Which sentence is written correctly?',
      opts: s.opts, a: s.a,
      why: '<p>This item tests <strong>' + G.q + '</strong>.</p><p>' + G.why + '</p>' +
        '<p>Read every option aloud in your head before choosing. Errors of this type are far easier to hear than to see.</p>',
      refs: [REF.purdue]
    };
  }

  function termTemplate(pool, section, refs, label) {
    return function (r, d) {
      var T = pick(r, pool);
      var s = shuffleWithAnswer(r, T.d, T.wrong, 4);
      return {
        section: section, difficulty: d, type: 'mcq',
        q: T.t + ' is best defined as:',
        opts: s.opts, a: s.a,
        why: '<p><strong>' + T.t + '</strong> — ' + T.d + '.</p>' +
          '<p>The distractors here are all real concepts from the same domain, attached to the wrong label. That is exactly how the live exam builds this item type, so learn each term <em>against its neighbours</em> rather than in isolation.</p>',
        refs: refs.concat([yt(T.t + ' ' + label + ' explained', 'Video — ' + T.t)])
      };
    };
  }

  function tEarnedValue(r, d) {
    var ev = intBetween(r, 4, 20) * 10000;
    var ac = ev + intBetween(r, -6, 8) * 5000;
    if (ac === ev) ac = ev + 10000;
    var cpi = +(ev / ac).toFixed(2);
    var over = cpi < 1;
    var correct = over ? 'Over budget' : 'Under budget';
    var s = shuffleWithAnswer(r, correct, ['On budget exactly', 'Behind schedule', 'Ahead of schedule'], 4);
    return {
      section: 'business', difficulty: d, type: 'mcq',
      q: 'A project reports an earned value of $' + ev.toLocaleString('en-US') + ' and an actual cost of $' + ac.toLocaleString('en-US') + '. What does the cost performance index indicate?',
      opts: s.opts, a: s.a,
      why: '<p>CPI = EV \u00f7 AC = ' + ev.toLocaleString('en-US') + ' \u00f7 ' + ac.toLocaleString('en-US') + ' = <strong>' + cpi + '</strong></p>' +
        '<p>A CPI ' + (over ? 'below' : 'at or above') + ' 1.0 means the project is <strong>' + correct.toLowerCase() + '</strong>.</p>' +
        '<p>Schedule is a separate index — SPI = EV \u00f7 PV — so cost data alone can never tell you whether the project is ahead or behind schedule. Two of the options here are schedule answers to a cost question, which is the trap.</p>',
      refs: [REF.pmi, yt('CPI SPI earned value formulas PMP', 'Video — Earned value indices')]
    };
  }

  /* =====================================================================
     FAMILY MAP
     ===================================================================== */

  var F = {
    clinical: [tLabValue, tPrecaution, tDrug, tVital],
    verbal: [tVocab, tGrammar],
    pm: [termTemplate(PM_TERMS, 'process', [REF.pmi], 'project management'), tEarnedValue],
    it: [termTemplate(IT_TERMS, 'networking', [REF.nist], 'CompTIA')],
    counseling: [termTemplate(COUNSEL, 'theories', [REF.aca], 'counselling')],
    realestate: [termTemplate(RE_TERMS, 'principles', [REF.hud], 'real estate')]
  };

  /* Exams whose filler should be clinical, verbal, etc. Anything absent
     falls through to the base numerical generator. */
  var FAMILY = {
    'ati-teas': ['clinical', 'verbal'],
    'hesi': ['clinical', 'verbal'],
    'nclex': ['clinical'],
    'dosage-calculation': null,
    'fnp': ['clinical'],
    'nremt': ['clinical'],
    'medical-assistant': ['clinical'],
    'phlebotomy': ['clinical'],
    'knat': ['clinical', 'verbal'],
    'nln-nex': ['clinical', 'verbal'],
    'ati-pharmacology': ['clinical'],
    'ati-med-surg': ['clinical'],
    'ati-anatomy': ['clinical'],
    'ati-fundamentals': ['clinical'],
    'ati-maternal': ['clinical'],
    'ati-pediatric': ['clinical'],
    'ati-mental-health': ['clinical'],
    'ati-community': ['clinical'],
    'ati-leadership': ['clinical'],
    'ati-nutrition': ['clinical'],
    'ged': ['verbal'],
    'gre': ['verbal'],
    'gmat': ['verbal'],
    'lsat': ['verbal'],
    'toefl': ['verbal'],
    'clep': ['verbal'],
    'tsi': ['verbal'],
    'asvab': ['verbal'],
    'ccat': ['verbal'],
    'wonderlic': ['verbal'],
    'pmp': ['pm'],
    'comptia': ['it'],
    'nce': ['counseling'],
    'real-estate': ['realestate'],
    'wgu': ['verbal']
  };

  var baseGenerate = BASE.generate;

  function generate(o) {
    o = o || {};
    var examId = (o.course && o.course.id) || o.prefix || '';
    var fams = FAMILY[examId];
    if (!fams || !fams.length) return baseGenerate(o);

    var pool = [];
    fams.forEach(function (f) { pool = pool.concat(F[f] || []); });
    if (!pool.length) return baseGenerate(o);

    var count = Math.max(1, o.count || 10);
    var r = rng(o.seed || 20260726);
    var diffs = ['easy', 'moderate', 'hard', 'advanced'];

    /* Blend: roughly two thirds subject items, one third quantitative, so
       maths-bearing exams keep their numerical coverage. Pure-verbal exams
       such as the LSAT get no injected arithmetic. */
    var quantShare = (examId === 'lsat' || examId === 'toefl' || examId === 'nce') ? 0 : 0.34;
    var quantCount = Math.round(count * quantShare);
    var subjCount = count - quantCount;

    var out = [];
    var seen = {};
    for (var i = 0; i < subjCount; i++) {
      var d = o.difficulty && o.difficulty !== 'combined' ? o.difficulty : diffs[i % 4];
      var item = null;
      /* Rotate templates, and retry a few times when a template hands back a
         stem already used in this paper. Small data pools otherwise repeat
         within a single sitting, which reads as a bug to the student. */
      for (var attempt = 0; attempt < 14 && !item; attempt++) {
        var tmpl = pool[(i + attempt) % pool.length];
        var cand;
        try { cand = tmpl(r, d); } catch (e) { continue; }
        if (!cand || seen[cand.q]) { cand = null; continue; }
        item = cand;
      }
      if (!item) continue;
      seen[item.q] = true;
      item.id = (o.prefix || 'gen') + '-s' + (o.seed || 0) + '-' + i;
      item.exam = examId;
      item.generated = true;
      out.push(item);
    }

    /* If the subject pools ran dry before the requested count, top up from
       the base generator rather than returning a short paper. */
    if (out.length < subjCount) {
      out = out.concat(baseGenerate({
        count: subjCount - out.length, seed: (o.seed || 0) + 3301,
        difficulty: o.difficulty, course: o.course, prefix: (o.prefix || 'gen') + 'f'
      }));
    }

    if (quantCount > 0) {
      var q = baseGenerate({
        count: quantCount, seed: (o.seed || 0) + 7919,
        difficulty: o.difficulty, course: o.course, prefix: (o.prefix || 'gen') + 'q'
      });
      out = out.concat(q);
    }

    /* Interleave so the paper does not run subject-then-maths in blocks */
    for (var j = out.length - 1; j > 0; j--) {
      var k = Math.floor(r() * (j + 1));
      var t = out[j]; out[j] = out[k]; out[k] = t;
    }
    return out.slice(0, count);
  }

  window.XP_GEN = { generate: generate, rng: rng, base: baseGenerate };
})();
